export const downloadFormativeMarksheetPdf = async ({
  institution,
  facultyName,
  departmentName,
  programName,
  unitName,
  unitCode,
  semesterName,
  academicYear,
  schedules = [],
  students = []
}) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = 297;
  const pageHeight = 210;
  const centerX = pageWidth / 2; // 148.5 mm

  // Helper for computing totals
  const computeRawTotal = (item) => {
    if (!item?.marks) return 0;
    const sum = Object.values(item.marks).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
    return Math.round(sum * 10) / 10;
  };

  const computeConvertedTotal = (item) => {
    if (!item?.marks || schedules.length === 0) return 0;
    const rawSum = computeRawTotal(item);
    const maxRaw = schedules.length * 30;
    if (maxRaw <= 0) return 0;
    return Math.round((rawSum / maxRaw) * 60);
  };

  // Header Start
  let y = 14;

  // Institution Logo if present
  if (institution?.logo_url) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = institution.logo_url;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      if (img.width && img.height) {
        const logoHeight = 16;
        const logoWidth = (img.width / img.height) * logoHeight;
        pdf.addImage(img, 'PNG', centerX - (logoWidth / 2), y, logoWidth, logoHeight);
        y += logoHeight + 6;
      }
    } catch (e) {}
  }

  pdf.setFont('times', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(0, 0, 0);
  pdf.text((institution?.name || 'UNIVERSITY OF EMBU').toUpperCase(), centerX, y, { align: 'center' });
  y += 6;

  if (facultyName) {
    pdf.setFontSize(11);
    pdf.text(facultyName.toUpperCase(), centerX, y, { align: 'center' });
    y += 6;
  }

  if (departmentName) {
    pdf.setFontSize(10.5);
    pdf.text(departmentName.toUpperCase(), centerX, y, { align: 'center' });
    y += 5;
  }

  // FORMATIVE MARKSHEET
  pdf.setFontSize(11);
  const titleText = 'FORMATIVE MARKSHEET';
  pdf.text(titleText, centerX, y, { align: 'center' });
  const titleWidth = pdf.getTextWidth(titleText);
  pdf.line(centerX - titleWidth / 2, y + 0.8, centerX + titleWidth / 2, y + 0.8);
  y += 5;

  // Semester Examinations
  const semesterHeading = semesterName 
    ? `${semesterName.toUpperCase().replace(/SEMESTER/i, '').trim()} SEMESTER EXAMINATIONS`
    : 'SEMESTER EXAMINATIONS';
  pdf.setFontSize(10.5);
  pdf.text(semesterHeading, centerX, y, { align: 'center' });
  const semWidth = pdf.getTextWidth(semesterHeading);
  pdf.line(centerX - semWidth / 2, y + 0.8, centerX + semWidth / 2, y + 0.8);
  y += 5;

  // Academic Year
  if (academicYear) {
    pdf.text(academicYear, centerX, y, { align: 'center' });
    const yearWidth = pdf.getTextWidth(academicYear);
    pdf.line(centerX - yearWidth / 2, y + 0.8, centerX + yearWidth / 2, y + 0.8);
    y += 5;
  }

  // Program Name
  if (programName) {
    pdf.text(programName.toUpperCase(), centerX, y, { align: 'center' });
    y += 5;
  }

  // Unit Name and Unit Code
  const unitLine = `${unitName ? `${unitName} - ` : ''}${unitCode || ''}`.toUpperCase();
  pdf.text(unitLine, centerX, y, { align: 'center' });
  y += 4;

  // Divider Line
  pdf.setLineWidth(0.5);
  pdf.line(15, y, 282, y);
  y += 4;

  // Table Columns Setup
  const leftMargin = 15;
  const rightMargin = 282;
  const tableWidth = rightMargin - leftMargin; // 267 mm

  const colSerialW = 18;
  const colNameW = 75;
  const colRegW = 55;
  const numCams = schedules.length || 1;
  const remainingW = tableWidth - colSerialW - colNameW - colRegW;
  const colTotalW = 28;
  const colCamW = Math.max(22, (remainingW - colTotalW) / numCams);

  const colX = [
    leftMargin,
    leftMargin + colSerialW,
    leftMargin + colSerialW + colNameW,
    leftMargin + colSerialW + colNameW + colRegW
  ];
  for (let i = 0; i < numCams; i++) {
    colX.push(colX[3 + i] + colCamW);
  }
  const totalX = colX[colX.length - 1];

  // Table Header Row (No vertical grid lines)
  const headerH = 8;
  pdf.setFillColor(235, 235, 235); // Light gray header background fill
  pdf.rect(leftMargin, y, tableWidth, headerH, 'F');
  
  // Solid top and bottom header lines
  pdf.setLineWidth(0.6);
  pdf.line(leftMargin, y, rightMargin, y);
  pdf.line(leftMargin, y + headerH, rightMargin, y + headerH);

  pdf.setFont('times', 'bold');
  pdf.setFontSize(9.5);

  pdf.text('Serial No#', colX[0] + colSerialW / 2, y + 5.5, { align: 'center' });
  pdf.text('Name Of Student', colX[1] + 3, y + 5.5);
  pdf.text('REG NO', colX[2] + 3, y + 5.5);

  schedules.forEach((sched, idx) => {
    const x = colX[3 + idx];
    pdf.text(`${sched.title || 'CAM'} (/30)`, x + colCamW / 2, y + 5.5, { align: 'center' });
  });

  pdf.text('Total (/60)', totalX + colTotalW / 2, y + 5.5, { align: 'center' });

  y += headerH;

  // Student Rows (No vertical gridlines, dotted bottom borders, solid last line)
  pdf.setFont('times', 'normal');
  pdf.setFontSize(9);
  const rowH = 6.5;

  students.forEach((student, idx) => {
    if (y + rowH > pageHeight - 35) {
      pdf.addPage('a4', 'landscape');
      y = 15;
    }

    const converted = computeConvertedTotal(student);

    pdf.text(String(idx + 1), colX[0] + colSerialW / 2, y + 4.5, { align: 'center' });
    pdf.text(student.student?.full_name || '', colX[1] + 3, y + 4.5);
    pdf.text(student.student?.student_id || '', colX[2] + 3, y + 4.5);

    schedules.forEach((sched, sIdx) => {
      const x = colX[3 + sIdx];
      const mark = student.marks?.[sched.id];
      
      let displayVal = (mark === undefined || mark === null || mark === '') ? '--' : Number(mark).toFixed(2);
      if (displayVal !== '--') {
        const poe = student.poe?.[sched.id];
        if (poe === 'not_uploaded') displayVal = 'NP';
        else if (poe === 'rejected' || poe === 'awaiting_trainer') displayVal = 'NA';
      }

      pdf.text(displayVal, x + colCamW / 2, y + 4.5, { align: 'center' });
    });

    pdf.setFont('times', 'bold');
    pdf.text(String(converted), totalX + colTotalW / 2, y + 4.5, { align: 'center' });
    pdf.setFont('times', 'normal');

    // Bottom Border: Dotted for intermediate rows, solid for last row
    const isLastRow = idx === students.length - 1;
    if (isLastRow) {
      pdf.setLineWidth(0.6);
      pdf.setLineDash([], 0); // Solid line
      pdf.line(leftMargin, y + rowH, rightMargin, y + rowH);
    } else {
      pdf.setLineWidth(0.3);
      pdf.setLineDash([0.6, 0.8], 0); // Dotted line
      pdf.line(leftMargin, y + rowH, rightMargin, y + rowH);
      pdf.setLineDash([], 0); // Reset dash
    }

    y += rowH;
  });

  // Legend / Key
  if (y + 10 > pageHeight - 20) {
    pdf.addPage('a4', 'landscape');
    y = 15;
  } else {
    y += 8;
  }
  
  pdf.setFont('times', 'bold');
  pdf.setFontSize(9);
  pdf.text('KEY:', leftMargin, y);
  pdf.setFont('times', 'normal');
  pdf.text('-- : Missing CAM Marks', leftMargin + 10, y);
  pdf.text('NP - Not Present / POE Not Uploaded', leftMargin + 50, y);
  pdf.text('NA - POE Not Approved by Trainer', leftMargin + 110, y);
  
  y += 6;

  // Signatures Section
  if (y + 35 > pageHeight) {
    pdf.addPage('a4', 'landscape');
    y = 20;
  } else {
    y += 10;
  }

  const sigWidth = 70;
  const sigGap = 20;
  const sigStartX = leftMargin + 10;

  pdf.setFont('times', 'bold');
  pdf.setFontSize(9.5);

  pdf.text('TRAINER / LECTURER:', sigStartX, y);
  pdf.line(sigStartX, y + 10, sigStartX + sigWidth, y + 10);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(8.5);
  pdf.text('Name, Signature & Date', sigStartX, y + 14);

  const hodX = sigStartX + sigWidth + sigGap;
  pdf.setFont('times', 'bold');
  pdf.setFontSize(9.5);
  pdf.text('HEAD OF DEPARTMENT:', hodX, y);
  pdf.line(hodX, y + 10, hodX + sigWidth, y + 10);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(8.5);
  pdf.text('Name, Signature & Date', hodX, y + 14);

  const evX = hodX + sigWidth + sigGap;
  pdf.setFont('times', 'bold');
  pdf.setFontSize(9.5);
  pdf.text('EXTERNAL VERIFIER:', evX, y);
  pdf.line(evX, y + 10, evX + sigWidth, y + 10);
  pdf.setFont('times', 'normal');
  pdf.setFontSize(8.5);
  pdf.text('Name, Signature & Date', evX, y + 14);

  const sanitizedUnitCode = (unitCode || 'Unit').replace(/[^a-zA-Z0-9_-]/g, '_');
  pdf.save(`Formative_Marksheet_${sanitizedUnitCode}.pdf`);
};

export const downloadFormativeResultSlipPdf = async ({
  institution,
  facultyName,
  departmentName,
  studentName,
  registrationNumber,
  programName,
  yearOfStudy,
  academicYear,
  semesterName,
  unitData = []
}) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = 210;
  const pageHeight = 297;
  const centerX = pageWidth / 2; // 105 mm
  const leftMargin = 12;
  const rightMargin = 198;
  const tableWidth = rightMargin - leftMargin; // 186 mm

  let y = 12;

  const instName = institution?.name || 'UNIVERSITY OF EMBU';
  const instSubName = facultyName || departmentName || institution?.campus_name || 'TVET INSTITUTE';

  // 1. Institution Logo if present
  if (institution?.logo_url) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = institution.logo_url;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      if (img.width && img.height) {
        const logoHeight = 16;
        const logoWidth = (img.width / img.height) * logoHeight;
        pdf.addImage(img, 'PNG', centerX - (logoWidth / 2), y, logoWidth, logoHeight);
        y += logoHeight + 4;
      }
    } catch (e) {}
  }

  // 2. Dynamic Institution Name & Sub-header
  pdf.setFont('times', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(0, 0, 0);
  pdf.text(instName.toUpperCase(), centerX, y, { align: 'center' });
  y += 5.5;

  pdf.setFontSize(11.5);
  pdf.text(instSubName.toUpperCase(), centerX, y, { align: 'center' });
  y += 5;

  // Horizontal Divider Line
  pdf.setLineWidth(0.8);
  pdf.line(leftMargin, y, rightMargin, y);
  y += 6;

  // 3. Document Title
  pdf.setFontSize(11);
  pdf.setFont('times', 'bold');
  const titleStr = 'CBET FORMATIVE ASSESSMENT RESULT SLIP';
  pdf.text(titleStr, centerX, y, { align: 'center' });
  const titleWidth = pdf.getTextWidth(titleStr);
  pdf.line(centerX - titleWidth / 2, y + 0.8, centerX + titleWidth / 2, y + 0.8);
  y += 6;

  // Top border for student info block
  pdf.setLineWidth(0.5);
  pdf.line(leftMargin, y, rightMargin, y);
  y += 4.5;

  // 4. Student Information Block
  pdf.setFontSize(9.5);
  const infoLabelX = leftMargin + 2;
  const infoValX = leftMargin + 50;

  const infoRows = [
    { label: 'NAME OF STUDENT:', val: (studentName || 'N/A').toUpperCase() },
    { label: 'REG. NO:', val: registrationNumber || 'N/A' },
    { label: 'PROGRAM:', val: programName || 'N/A' },
    { label: 'YEAR OF STUDY:', val: yearOfStudy || semesterName || 'Year One Semester One' },
    { label: 'ACADEMIC YEAR:', val: academicYear || '2025/2026' }
  ];

  infoRows.forEach(row => {
    pdf.setFont('times', 'bold');
    pdf.text(row.label, infoLabelX, y);
    pdf.setFont('times', 'normal');
    pdf.text(row.val, infoValX, y);
    y += 4.5;
  });

  pdf.setLineWidth(0.5);
  pdf.line(leftMargin, y - 1, rightMargin, y - 1);
  y += 4;

  // Determine max CAMs across all units to dynamically render columns
  let maxCamsCount = 3;
  unitData.forEach(u => {
    if (u.cams && u.cams.length > maxCamsCount) maxCamsCount = u.cams.length;
    u.cams?.forEach(c => {
      if (c.targetCamNum > maxCamsCount) maxCamsCount = c.targetCamNum;
    });
  });
  maxCamsCount = Math.min(maxCamsCount, 10);

  // 5. Main Results Table (No vertical gridlines, dotted row bottom borders, solid final bottom border)
  // Dynamic column sizing based on total available width (186mm)
  let camColW = 22;
  if (maxCamsCount > 3) camColW = Math.max(13, Math.floor(80 / maxCamsCount));
  const titleColW = 141 - (camColW * maxCamsCount);

  const colW = [25, titleColW];
  for (let i = 0; i < maxCamsCount; i++) colW.push(camColW);
  colW.push(20); // TOTAL

  const colX = [leftMargin];
  for (let i = 0; i < colW.length; i++) {
    colX.push(colX[i] + colW[i]);
  }

  const headerH = 7.5;
  pdf.setFillColor(220, 220, 220); // Gray header background fill
  pdf.rect(leftMargin, y, tableWidth, headerH, 'F');
  
  // Solid top and bottom header lines
  pdf.setLineWidth(0.6);
  pdf.line(leftMargin, y, rightMargin, y);
  pdf.line(leftMargin, y + headerH, rightMargin, y + headerH);

  pdf.setFont('times', 'bold');
  pdf.setFontSize(8.5);

  const headers = ['UNIT CODE', 'UNIT TITLE'];
  for (let i = 0; i < maxCamsCount; i++) headers.push(`CAM ${i + 1} (/30)`);
  headers.push('TOTAL (/60)');
  headers.forEach((h, idx) => {
    const x = colX[idx];
    const w = colW[idx];
    const align = (idx === 0 || idx === 1) ? 'left' : 'center';
    const textX = align === 'left' ? x + 2 : x + w / 2;
    pdf.text(h, textX, y + 5, { align });
  });

  y += headerH;

  // Table Body Rows
  pdf.setFont('times', 'normal');
  pdf.setFontSize(8.5);
  const rowH = 6.5;

  unitData.forEach((u, rIdx) => {
    if (y + rowH > pageHeight - 55) {
      pdf.addPage('a4', 'portrait');
      y = 15;
    }

    const formatMarkStr = (camObj) => {
      if (!camObj) return '--';
      if (camObj.isMissingPoe) return 'NP'; // No POE
      if (camObj.marks === null || camObj.marks === undefined || isNaN(camObj.marks)) return '--';
      return String(camObj.marks);
    };

    const totalVal = u.hasMarks ? `${u.formativeTotal60}` : '--';

    // Cell Text
    pdf.text(u.unitCode || '', colX[0] + 2, y + 4.5);
    pdf.text(u.unitName || '', colX[1] + 2, y + 4.5);
    
    for (let i = 0; i < maxCamsCount; i++) {
      const targetCamNum = i + 1;
      const camObj = u.cams?.find(c => c.targetCamNum === targetCamNum);
      const strVal = formatMarkStr(camObj);
      pdf.text(strVal, colX[2 + i] + colW[2 + i] / 2, y + 4.5, { align: 'center' });
    }

    pdf.setFont('times', u.hasMarks ? 'bold' : 'normal');
    pdf.text(totalVal, colX[2 + maxCamsCount] + colW[2 + maxCamsCount] / 2, y + 4.5, { align: 'center' });
    pdf.setFont('times', 'normal');

    // Bottom Border: Dotted for intermediate rows, solid for last row
    const isLastRow = rIdx === unitData.length - 1;
    if (isLastRow) {
      pdf.setLineWidth(0.6);
      pdf.setLineDash([], 0); // Solid line
      pdf.line(leftMargin, y + rowH, rightMargin, y + rowH);
    } else {
      pdf.setLineWidth(0.3);
      pdf.setLineDash([0.6, 0.8], 0); // Dotted line
      pdf.line(leftMargin, y + rowH, rightMargin, y + rowH);
      pdf.setLineDash([], 0); // Reset dash
    }

    y += rowH;
  });

  // Total Units summary line
  y += 3.5;
  pdf.setFont('times', 'bold');
  pdf.setFontSize(9);
  pdf.text(`TOTAL UNITS: (${unitData.length})`, leftMargin, y);
  y += 3;

  // Dashed Line
  pdf.setLineDash([1, 1], 0);
  pdf.line(leftMargin, y, rightMargin, y);
  pdf.setLineDash([], 0); // Reset dash
  y += 6;

  // 6. Grading System Key Footer
  pdf.setFontSize(8.5);
  pdf.setFont('times', 'bold');
  pdf.text('Key To Grading System', leftMargin, y);
  pdf.text('Key To Letters Where There Is No Grade', leftMargin + 95, y);
  y += 3.5;

  pdf.line(leftMargin, y, leftMargin + 80, y);
  pdf.line(leftMargin + 95, y, rightMargin, y);
  y += 4;

  pdf.setFontSize(8);
  const leftKeys = [
    { range: '80 - 100%', label: 'Mastery' },
    { range: '65 - 79%', label: 'Proficient' },
    { range: '50 - 64%', label: 'Competent' },
    { range: '0 - 49%', label: 'Not Yet Competent' }
  ];

  const rightKeys = [
    { code: 'CRNM', desc: 'Course Requirement Not Met' },
    { code: 'EI', desc: 'Examination Irregularity' },
    { code: 'NP', desc: 'No POE Uploaded' },
    { code: '--', desc: 'Assessment Pending' }
  ];

  for (let i = 0; i < 4; i++) {
    const l = leftKeys[i];
    const r = rightKeys[i];

    if (l) {
      pdf.setFont('times', 'bold');
      pdf.text(l.range, leftMargin, y);
      pdf.setFont('times', 'normal');
      pdf.text(l.label, leftMargin + 25, y);
    }

    if (r) {
      pdf.setFont('times', 'bold');
      pdf.text(r.code, leftMargin + 95, y);
      pdf.setFont('times', 'normal');
      pdf.text(r.desc, leftMargin + 110, y);
    }
    y += 3.8;
  }

  y += 8;

  // 7. Dynamic Footnote Notice
  pdf.setFont('times', 'bolditalic');
  pdf.setFontSize(7.5);
  const noticeText = `NB: This is NOT a transcript. ${instName} reserves the right to correct the information given on the result slip, which will be confirmed by issue of a Transcript.`;
  const splitNotice = pdf.splitTextToSize(noticeText, tableWidth);
  pdf.text(splitNotice, centerX, y, { align: 'center' });

  // Download PDF
  const filename = `${studentName ? studentName.replace(/\s+/g, '_') : 'Student'}_Formative_Results_Slip.pdf`;
  pdf.save(filename);
};
